import * as React from "react";

import { PresentationFacet, ResponseFacetValue } from "../../model";

import { usePlpAnalytics } from "../plp-analytics-provider";

import {
  useProductListController_forFiltering,
  useProductListData,
  useProductListPresentationModelFacets,
} from "../product-list-provider";
import { IS_HORIZONTAL_FILTER_ENABLED } from "../../flags";

interface PlpPresenterForTheFilterMenu_DesktopConsumerInterface {
  shouldShowTheFilterMenu_onDesktop: boolean;
  handleTogglingTheFilterMenu_onDesktop: () => void;
  whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop: string | undefined;
  handleTogglingAFilterSubMenu_onDesktop: (facet: PresentationFacet) => void;
  handleActionFilterOption_onDesktop: (
    facet: PresentationFacet,
    option: ResponseFacetValue
  ) => void;
  setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}
const defaultDesktopValue =
  {} as unknown as PlpPresenterForTheFilterMenu_DesktopConsumerInterface;

const PlpPresenterForTheFilterMenu_DesktopContext =
  React.createContext(defaultDesktopValue);

export const PlpPresenterForTheFilterMenu_DesktopProvider: React.FunctionComponent =
  (props) => {
    const { analytics } = usePlpAnalytics();

    const { data: productListData } = useProductListData();
    const { presentationFacets } = useProductListPresentationModelFacets();

    const {
      isOptionSelected: productListController_forFiltering_isOptionSelected,
      handleActionFilterOption:
        productListController_forFiltering_handleActionFilterOption,
    } = useProductListController_forFiltering();

    const [
      shouldShowTheFilterMenu_onDesktop,
      setShouldShowTheFilterMenu_onDesktop,
    ] = React.useState(true);

    const [
      theLastKnown_firstPageResponseId,
      setTheLastKnown_firstPageResponseId,
    ] = React.useState<string | undefined>();

    const [
      whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
      setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
    ] = React.useState<string | undefined>();

    React.useEffect(() => {
      let firstPageResponseId = null;
      if ((productListData?.pages.length || 0) > 0) {
        const firstPage = productListData?.pages[0];
        if (firstPage) {
          firstPageResponseId = firstPage.response_id;
        }
      }
      if (
        firstPageResponseId &&
        firstPageResponseId !== theLastKnown_firstPageResponseId &&
        !whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop
      ) {
        if (!IS_HORIZONTAL_FILTER_ENABLED) {
          const defaultOpenFacet: string[] = [];
          presentationFacets.forEach((facet) => {
            if (facet.isOpenByDefault) {
              defaultOpenFacet.push(facet.key);
            }
          });
          if (defaultOpenFacet.length > 0) {
            setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop(
              defaultOpenFacet.join(",")
            );
          }
        }
        setTheLastKnown_firstPageResponseId(firstPageResponseId);
      }
    }, [
      whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
      productListData,
      presentationFacets,
    ]);

    const handleTogglingTheFilterMenu_onDesktop = React.useCallback(() => {
      setShouldShowTheFilterMenu_onDesktop(!shouldShowTheFilterMenu_onDesktop);
    }, [shouldShowTheFilterMenu_onDesktop]);

    const handleTogglingAFilterSubMenu_onDesktop = React.useCallback(
      (facet: PresentationFacet) => {
        if (IS_HORIZONTAL_FILTER_ENABLED) {
          setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop((prevKey) =>
            prevKey === facet.key ? undefined : facet.key
          );
          return;
        }

        let keyArray: string[] =
          whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop
            ? whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop.split(",")
            : [];

        if (keyArray?.includes(facet.key)) {
          keyArray = keyArray.filter((key) => key !== facet.key);
        } else if (Array.isArray(keyArray)) {
          keyArray[keyArray.length] = facet.key;
        } else {
          keyArray = [facet.key];
        }

        setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop(
          keyArray.join(",")
        );
      },
      [whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop]
    );

    const handleActionFilterOption_onDesktop = React.useCallback(
      (facet: PresentationFacet, option: ResponseFacetValue) => {
        const wasTheOptionOnWhenTheUserClickedOnIt =
          productListController_forFiltering_isOptionSelected(facet, option);
        const eventName = `bbnt-plp-product-list-view-fresh-results-observer-${
          (window as any).plpProviderId
        }-fresh-plp-loaded`;

        productListController_forFiltering_handleActionFilterOption(
          facet,
          option
        );

        const clickActionFunc = () => {
          analytics.clickActionOnFacet({
            facet,
            option,
            wasTheOptionOnWhenTheUserClickedOnIt,
            isMobile: false,
          });

          window.removeEventListener(eventName, clickActionFunc);
        };

        window.addEventListener(eventName, clickActionFunc);
      },
      [
        productListController_forFiltering_isOptionSelected,
        productListController_forFiltering_handleActionFilterOption,
      ]
    );

    const value: PlpPresenterForTheFilterMenu_DesktopConsumerInterface =
      React.useMemo(
        () => ({
          shouldShowTheFilterMenu_onDesktop,
          handleTogglingTheFilterMenu_onDesktop,

          whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
          handleTogglingAFilterSubMenu_onDesktop,

          handleActionFilterOption_onDesktop,
          setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
        }),
        [
          shouldShowTheFilterMenu_onDesktop,
          handleTogglingTheFilterMenu_onDesktop,

          whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
          handleTogglingAFilterSubMenu_onDesktop,

          handleActionFilterOption_onDesktop,
          setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
        ]
      );
    return (
      <PlpPresenterForTheFilterMenu_DesktopContext.Provider value={value}>
        {props.children || []}
      </PlpPresenterForTheFilterMenu_DesktopContext.Provider>
    );
  };

export const usePlpPresenterForTheFilterMenu_Desktop = () => {
  const value = React.useContext(PlpPresenterForTheFilterMenu_DesktopContext);
  if (value === defaultDesktopValue) {
    throw new Error(
      "usePlpPresenterForTheFilterMenu_Desktop must be used within a PlpPresenterForTheFilterMenu_DesktopProvider"
    );
  }
  return value;
};
